import React, { createContext, useContext, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { authFetch } from '../common/authFetch';

const GoalContext = createContext();

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const GoalProvider = ({ children }) => {
  const { t } = useTranslation();
  const [goals, setGoals] = useState([]);
  const [progress, setProgress] = useState(null);
  const [goalLoading, setGoalLoading] = useState(false);

  const fetchGoals = useCallback(async () => {
    try {
      setGoalLoading(true);
      const response = await authFetch(`${API_URL}/api/v1/study_goals`);
      const data = await response.json();
      if (data.success) {
        setGoals(data.goals || []);
      }
    } catch (error) {
      console.error('Error fetching goals:', error);
    } finally {
      setGoalLoading(false);
    }
  }, []);

  const fetchProgress = useCallback(async (periodType, date) => {
    try {
      setGoalLoading(true);
      const params = new URLSearchParams();
      if (periodType) params.append('period_type', periodType);
      if (date) params.append('date', date);

      const response = await authFetch(`${API_URL}/api/v1/study_goals/progress?${params.toString()}`);
      const data = await response.json();
      if (data.success) {
        setProgress(data.progress);
      }
    } catch (error) {
      console.error('Error fetching goal progress:', error);
    } finally {
      setGoalLoading(false);
    }
  }, []);

  const fetchAllProgress = useCallback(async () => {
    try {
      setGoalLoading(true);
      const today = new Date().toISOString().split('T')[0];
      const types = ['daily', 'weekly', 'monthly', 'custom'];
      const results = await Promise.all(
        types.map(async (type) => {
          const params = new URLSearchParams({ period_type: type });
          if (type !== 'custom') params.append('date', today);
          const response = await authFetch(`${API_URL}/api/v1/study_goals/progress?${params.toString()}`);
          const data = await response.json();
          return data.success ? (data.progress?.goals || []) : [];
        })
      );
      const allGoals = results.flat();
      setProgress({ period_type: 'all', goals: allGoals });
    } catch (error) {
      console.error('Error fetching all progress:', error);
    } finally {
      setGoalLoading(false);
    }
  }, []);

  const createGoal = useCallback(async (goalData) => {
    try {
      const response = await authFetch(`${API_URL}/api/v1/study_goals`, {
        method: 'POST',
        body: JSON.stringify(goalData)
      });
      const data = await response.json();
      if (data.success) {
        toast.success(t('saved'));
        await fetchGoals();
        return data.goal;
      } else {
        toast.error(data.message || t('error.save'));
        return null;
      }
    } catch (error) {
      console.error('Error creating goal:', error);
      toast.error(t('error.save'));
      return null;
    }
  }, [t, fetchGoals]);

  const updateGoal = useCallback(async (goalId, goalData) => {
    try {
      const response = await authFetch(`${API_URL}/api/v1/study_goals/${goalId}`, {
        method: 'PUT',
        body: JSON.stringify(goalData)
      });
      const data = await response.json();
      if (data.success) {
        toast.success(t('saved'));
        await fetchGoals();
        return true;
      } else {
        toast.error(data.message || t('error.save'));
        return false;
      }
    } catch (error) {
      console.error('Error updating goal:', error);
      toast.error(t('error.save'));
      return false;
    }
  }, [t, fetchGoals]);

  const deleteGoal = useCallback(async (goalId) => {
    try {
      const response = await authFetch(`${API_URL}/api/v1/study_goals/${goalId}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        toast.success(t('deleted'));
        await fetchGoals();
        return true;
      } else {
        toast.error(data.message || t('error.save'));
        return false;
      }
    } catch (error) {
      console.error('Error deleting goal:', error);
      toast.error(t('error.save'));
      return false;
    }
  }, [t, fetchGoals]);

  const value = {
    goals,
    progress,
    goalLoading,
    fetchGoals,
    fetchProgress,
    fetchAllProgress,
    createGoal,
    updateGoal,
    deleteGoal
  };

  return (
    <GoalContext.Provider value={value}>
      {children}
    </GoalContext.Provider>
  );
};

export const useGoals = () => {
  const context = useContext(GoalContext);
  if (!context) {
    throw new Error('useGoals must be used within a GoalProvider');
  }
  return context;
};
